import React, {PureComponent} from "react"

import Flex from "@irati/ui/src/Flex"
import {H2} from "@irati/ui/src/Typography"
import Card from "@irati/ui/src/Layout/Card"
import {LangLoader, FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import Link from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Loading from "@irati/ui/src/Loading"
import Slider from "@irati/ui/src/Slider"
import GlobalStyle, {DealContainer} from "@irati-web/perks/src/components/FeedSlider/styled/DealContainer"
import DealCard from "@irati-web/perks/src/components/Deal/DealCard"
import dealsService from "@irati/deals/services/dealsGraph"
import buService from "@irati/billing/services/bu"
import authentication from "@irati/auth"
import regionService from "@irati/region/services/regionData"
import {Deal} from "@irati/types"
import {adminDashboardTracking} from "@irati-tracking/admin"
import {PERKS} from "@irati-web/app/src/routeConfig"
import Checkbox from "@irati/ui/src/FormItem/Checkbox"

import CountryCard from "./CountryCard"

interface Props extends InjectIntl {}

interface State {
  load: Promise<void> | null
  data: Deal[]
  isRewards: boolean
}

const preferencedRegionIds = ["UK", "AU", "US", "IRE", "FR", "NZ", "ES", "DE", "IN", "SG", "NL", "IT", "PH", "PT"]
const TOTAL_DEALS_TO_RETRIEVE = 4
const SLIDER_SETTINGS = {
  slidesToShow: 3.25,
  slidesToScroll: 3
}

class GlobalPerksRewardsCatalogue extends PureComponent<Props, State> {
  state = {
    load: null,
    data: [],
    isRewards: false
  }

  componentDidMount() {
    this.fetchDeals()
  }

  fetchDeals = () => {
    const load = buService.list({businessId: authentication.business().uuid}).then(async (bus) => {
      const regions = await regionService.getAllRegions()
      const businessUnits = bus.filter(({regionId}) => regionId)
      const originalRegionIds = businessUnits.length ? bus.filter((t) => t?.regionId).map(({regionId}) => regionId) : preferencedRegionIds
      const regionIds = [...originalRegionIds]
      const results = await this.buildCard(1, [], regionIds, originalRegionIds, regions)
      this.setState({data: results})
    })
    this.setState({load})
  }

  buildCard = async (amount, results, regionIds, originalRegionIds, regions) => {
    const deal = await this.getDeal(
      amount,
      regionIds,
      results.map(({uuid}) => uuid)
    )
    const regionTest = this.getUpdatedRegionIds(deal, regionIds, originalRegionIds)

    const updatedRegionIds = regionTest.regionIds
    const dealRegionId = regionTest.regionId
    const region = regions.find(({id}) => id === dealRegionId)
    const updatedResults = [...results, {isRegion: true, region}, deal]

    if (amount === TOTAL_DEALS_TO_RETRIEVE) {
      return updatedResults
    }
    return this.buildCard(amount + 1, updatedResults, updatedRegionIds, originalRegionIds, regions)
  }

  getDeal = async (limit: number, regionIds: string[], existingIds: string[]) => {
    const {isRewards} = this.state
    return dealsService
      .list({
        hideEB: true,
        regions: regionIds,
        isRewards,
        limit
      })
      .then((data) => {
        if (limit === 1) {
          return data.results[0]
        }
        return data.results.find((deal) => !existingIds.includes(deal.uuid))
      })
  }

  getUpdatedRegionIds = (deal: Deal, regionIds: string[], originalRegionIds: string[]) => {
    const regionId = deal?.regionIds.find((id) => regionIds.includes(id)) || regionIds[0]
    const index = regionIds.indexOf(regionId)
    if (index > -1) {
      regionIds.splice(index, 1)
    }
    if (regionIds.length === 0) {
      return {
        regionIds: preferencedRegionIds.filter((id) => !originalRegionIds.includes(id)),
        regionId
      }
    }
    return {regionIds, regionId}
  }

  updateIsRewardsCheckbox = () => {
    this.setState((prevState) => ({isRewards: !prevState.isRewards}))
    this.fetchDeals()
  }

  render() {
    const {load, data, isRewards} = this.state
    const {formatMessage} = this.props

    return (
      <LangLoader bucket={PERKS}>
        <GlobalStyle />
        <Card marginBottom={2}>
          <Flex justifyContent="space-between" alignItems="flex-end" marginBottom={2}>
            <Flex alignItems="center">
              <H2>
                <FormattedMessage id="admin.dashboard.perksCatalogue.title" />
              </H2>
              <Checkbox name="isRewards" flush checked={isRewards} onChange={this.updateIsRewardsCheckbox}>
                <FormattedMessage id="admin.dashboard.perksCatalogue.checkbox" />
              </Checkbox>
            </Flex>
            <Link
              onClick={adminDashboardTracking.clickCatalogueSeeAllDeals}
              cta
              href={formatMessage({id: "admin.dashboard.perksCatalogue.link"})}
              target="_blank">
              <Flex alignItems="center">
                <FormattedMessage id="admin.dashboard.perksCatalogue.button" />
                <Icon icon={IconTypes.arrowRight} small marginLeft />
              </Flex>
            </Link>
          </Flex>
          <Loading load={load}>
            <Slider onClick={adminDashboardTracking.clickCatalogueSliderArrows} settings={SLIDER_SETTINGS}>
              {data.map((item, key) =>
                item?.isRegion ? (
                  <CountryCard region={item.region} />
                ) : (
                  <DealContainer key={item.uuid}>
                    <div onClick={adminDashboardTracking.clickCatalogueDeal}>
                      <DealCard
                        deal={item}
                        useWalletDealCard={isRewards}
                        trackingData={{
                          position: key + 1,
                          sectionHeader: formatMessage({id: "admin.dashboard.perksCatalogue.title"})
                        }}
                      />
                    </div>
                  </DealContainer>
                )
              )}
            </Slider>
          </Loading>
        </Card>
      </LangLoader>
    )
  }
}

export default injectIntl(GlobalPerksRewardsCatalogue)
